import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useAuth } from '../components/AuthProvider';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL;

const AdminConfigScreen: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, clientId, adminEmail } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availableCarrierOptions, setAvailableCarrierOptions] = useState<{ id: string; type: string; readable: string }[]>([]);

  const [config, setConfig] = useState<{
    shipping_method: 'flat_rate' | 'carrier';
    free_shipping_threshold: string;
    flat_rate_type: 'amount' | 'percentage';
    flat_rate_value: string;
    selected_carrier_services: string[];
  }>({
    shipping_method: 'flat_rate',
    free_shipping_threshold: '',
    flat_rate_type: 'amount',
    flat_rate_value: '',
    selected_carrier_services: [],
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });

  useEffect(() => {
    if (!isAdmin || !clientId) {
      navigate('/login');
      return;
    }

    const fetchConfig = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/config`, {
          headers: {
            'Content-Type': 'application/json',
            'x-client-id': clientId,
          },
        });
        const data = await res.json();
        setConfig((prev) => ({ ...prev, ...data }));
      } catch (err) {
        console.error('Failed to fetch config', err);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [isAdmin, clientId, navigate]);

  useEffect(() => {
    if (config.shipping_method === 'carrier') {
      fetch(`${API_BASE}/api/shipping/carrier-list`)
        .then((res) => res.json())
        .then((data) => setAvailableCarrierOptions(data))
        .catch((err) => {
          console.error('Failed to fetch carrier options', err);
        });
    }
  }, [config.shipping_method]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-client-id': clientId!,
        },
        body: JSON.stringify(config),
      });
      if (!res.ok) throw new Error('Failed to save config');
      alert('Settings saved.');
    } catch (err) {
      console.error(err);
      alert('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
      alert('Passwords do not match.');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/admin/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-client-id': clientId!,
          'x-admin-email': adminEmail || '',
        },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        }),
      });

      if (!res.ok) throw new Error('Password change failed');
      alert('Password updated.');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
    } catch (err) {
      console.error(err);
      alert('Failed to update password.');
    }
  };

  const toggleCarrierOption = (id: string) => {
    setConfig((prev) => {
      const selected = new Set(prev.selected_carrier_services);
      selected.has(id) ? selected.delete(id) : selected.add(id);
      return { ...prev, selected_carrier_services: Array.from(selected) };
    });
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Admin Configuration
      </Typography>

      <Box mt={4}>
        <Typography variant="h6">Change Password</Typography>
        <Box display="flex" flexDirection="column" gap={2} mt={2}>
          <TextField
            label="Current Password"
            type="password"
            value={passwordForm.currentPassword}
            onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
            fullWidth
          />
          <TextField
            label="New Password"
            type="password"
            value={passwordForm.newPassword}
            onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
            fullWidth
          />
          <TextField
            label="Confirm New Password"
            type="password"
            value={passwordForm.confirmNewPassword}
            onChange={(e) =>
              setPasswordForm({ ...passwordForm, confirmNewPassword: e.target.value })
            }
            fullWidth
          />
          <Button variant="contained" onClick={handlePasswordChange}>
            Change Password
          </Button>
        </Box>
      </Box>

      <Box mt={6}>
        <Typography variant="h6">Shipping Settings</Typography>

        <TextField
          label="Free Shipping Threshold ($)"
          type="number"
          value={config.free_shipping_threshold}
          onChange={(e) => setConfig({ ...config, free_shipping_threshold: e.target.value })}
          fullWidth
          sx={{ mt: 2 }}
        />

        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Shipping Method</InputLabel>
          <Select
            value={config.shipping_method}
            onChange={(e) =>
              setConfig({
                ...config,
                shipping_method: e.target.value as 'flat_rate' | 'carrier',
              })
            }
            label="Shipping Method"
          >
            <MenuItem value="flat_rate">Flat Rate</MenuItem>
            <MenuItem value="carrier">Carrier-Based</MenuItem>
          </Select>
        </FormControl>

        {config.shipping_method === 'flat_rate' && (
          <Box display="flex" gap={2} mt={2}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={config.flat_rate_type}
                onChange={(e) =>
                  setConfig({ ...config, flat_rate_type: e.target.value as 'amount' | 'percentage' })
                }
                label="Type"
              >
                <MenuItem value="amount">Amount</MenuItem>
                <MenuItem value="percentage">Percentage</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Flat Rate Value"
              type="number"
              value={config.flat_rate_value}
              onChange={(e) => setConfig({ ...config, flat_rate_value: e.target.value })}
            />
          </Box>
        )}

        {config.shipping_method === 'carrier' && (
          <Box mt={2}>
            {availableCarrierOptions.length > 0 ? (
              availableCarrierOptions.map((opt) => (
                <FormControlLabel
                  key={opt.id}
                  control={
                    <Checkbox
                      checked={config.selected_carrier_services.includes(opt.id)}
                      onChange={() => toggleCarrierOption(opt.id)}
                    />
                  }
                  label={opt.readable}
                />
              ))
            ) : (
              <Typography>No carrier services available.</Typography>
            )}
          </Box>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          sx={{ mt: 3 }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Container>
  );
};

export default AdminConfigScreen;
